import React, { useState, useEffect } from 'react';
import { Car, Flag, Trophy, RotateCcw, Target, ChevronUp, ChevronDown } from 'lucide-react';

export const RacingGame = () => {
  const [playerPosition, setPlayerPosition] = useState(0);
  const [computerPosition, setComputerPosition] = useState(0);
  const [gameStatus, setGameStatus] = useState('ready'); // ready, racing, finished
  const [winner, setWinner] = useState(null);
  const [playerScore, setPlayerScore] = useState(0);
  const [computerScore, setComputerScore] = useState(0);
  const [difficulty, setDifficulty] = useState(1);
  const [spaceKeyPressed, setSpaceKeyPressed] = useState(false);

  const FINISH_LINE = 85; // percentage of track width
  const PLAYER_SPEED = 3;

  // Difficulty settings
  const DIFFICULTIES = {
    1: { name: 'Very Easy', speed: 0.3, variation: 0.1, color: 'text-green-500' },
    2: { name: 'Easy', speed: 0.5, variation: 0.2, color: 'text-blue-500' },
    3: { name: 'Medium', speed: 0.8, variation: 0.3, color: 'text-yellow-500' },
    4: { name: 'Hard', speed: 1.2, variation: 0.4, color: 'text-orange-500' },
    5: { name: 'Very Hard', speed: 1.6, variation: 0.5, color: 'text-red-500' },
    6: { name: 'Extreme', speed: 2.0, variation: 0.6, color: 'text-purple-500' },
    7: { name: 'Impossible', speed: 2.5, variation: 0.7, color: 'text-pink-500' }
  };

  const currentDifficulty = DIFFICULTIES[difficulty];

  // Computer AI movement
  useEffect(() => {
    if (gameStatus === 'racing') {
      const moveComputer = () => {
        setComputerPosition(prev => {
          const baseSpeed = currentDifficulty.speed;
          const variation = currentDifficulty.variation;
          const newPosition = prev + baseSpeed + (Math.random() * variation - variation/2);
          if (newPosition >= FINISH_LINE) {
            setGameStatus('finished');
            setWinner('computer');
            setComputerScore(score => score + 1);
            return FINISH_LINE;
          }
          return newPosition;
        });
      };

      const interval = setInterval(moveComputer, 50);
      return () => clearInterval(interval);
    }
  }, [gameStatus, currentDifficulty]);

  const movePlayer = () => {
    if (gameStatus === 'racing') {
      setPlayerPosition(prev => {
        const newPosition = prev + PLAYER_SPEED;
        if (newPosition >= FINISH_LINE) {
          setGameStatus('finished');
          setWinner('player');
          setPlayerScore(score => score + 1);
          return FINISH_LINE;
        }
        return newPosition;
      });
    }
  };

  const startRace = () => {
    setGameStatus('racing');
    setPlayerPosition(0);
    setComputerPosition(0);
    setWinner(null);
  };

  const increaseDifficulty = () => {
    if (difficulty < 7) {
      setDifficulty(prev => prev + 1);
    }
  };

  const decreaseDifficulty = () => {
    if (difficulty > 1) {
      setDifficulty(prev => prev - 1);
    }
  };

  const resetGame = () => {
    setGameStatus('ready');
    setPlayerPosition(0);
    setComputerPosition(0);
    setWinner(null);
    setPlayerScore(0);
    setComputerScore(0);
  };

  // Handle keyboard input
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space' || e.key === ' ') {
        e.preventDefault();

        // Only trigger if space wasn't already pressed (prevents holding)
        if (!spaceKeyPressed) {
          setSpaceKeyPressed(true);

          if (gameStatus === 'ready') {
            startRace();
          } else if (gameStatus === 'racing') {
            movePlayer();
          } else if (gameStatus === 'finished') {
            startRace();
          }
        }
      }
    };

    const handleKeyUp = (e) => {
      if (e.code === 'Space' || e.key === ' ') {
        setSpaceKeyPressed(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [gameStatus, spaceKeyPressed]);

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold flex items-center">
          <Car className="mr-2" size={24} />
          Racing Game
        </h3>
        <div className="flex items-center space-x-4">
          <div className="text-sm">
            <span className="font-medium">Player:</span> {playerScore}
          </div>
          <div className="text-sm">
            <span className="font-medium">Computer:</span> {computerScore}
          </div>
        </div>
      </div>

      {/* Difficulty Selector */}
      <div className="bg-gray-50 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Target className="mr-2" size={20} />
            <span className="font-medium">Difficulty:</span>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={decreaseDifficulty}
              disabled={difficulty === 1}
              className="p-1 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronDown size={16} />
            </button>
            <div className="flex items-center space-x-2">
              <span className="font-bold">Level {difficulty}</span>
              <span className={`font-semibold ${currentDifficulty.color}`}>
                {currentDifficulty.name}
              </span>
            </div>
            <button
              onClick={increaseDifficulty}
              disabled={difficulty === 7}
              className="p-1 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronUp size={16} />
            </button>
          </div>
        </div>
        <div className="mt-2 text-sm text-gray-600">
          Computer speed: {currentDifficulty.speed.toFixed(1)}x | Variation: ±{(currentDifficulty.variation/2).toFixed(1)}
        </div>
      </div>

      {/* Game Track */}
      <div className="relative bg-gray-800 rounded-lg p-4 mb-4" style={{ height: '200px' }}>
        {/* Track lines */}
        <div className="absolute inset-0 flex flex-col justify-center">
          <div className="border-t-2 border-dashed border-yellow-300 opacity-50"></div>
          <div className="border-t-2 border-dashed border-yellow-300 opacity-50 mt-8"></div>
        </div>

        {/* Finish Line */}
        <div
          className="absolute top-0 bottom-0 w-1 bg-yellow-400 flex items-center justify-center"
          style={{ left: `${FINISH_LINE}%` }}
        >
          <Flag size={16} className="text-black" />
        </div>

        {/* Player Car */}
        <div
          className="absolute top-8 transition-all duration-100 ease-out"
          style={{ left: `${playerPosition}%` }}
        >
          <div className="bg-blue-500 rounded-lg p-2 shadow-lg">
            <Car size={24} className="text-white" />
          </div>
          <div className="text-xs text-white text-center mt-1">You</div>
        </div>

        {/* Computer Car */}
        <div
          className="absolute bottom-8 transition-all duration-100 ease-out"
          style={{ left: `${computerPosition}%` }}
        >
          <div className="bg-red-500 rounded-lg p-2 shadow-lg">
            <Car size={24} className="text-white" />
          </div>
          <div className="text-xs text-white text-center mt-1">CPU</div>
        </div>
      </div>

      {/* Game Status */}
      <div className="text-center mb-4">
        {gameStatus === 'ready' && (
          <div>
            <p className="text-lg font-semibold mb-2">Ready to Race!</p>
            <p className="text-sm text-gray-600 mb-2">
              Press SPACE or click "Start Race" to begin. Keep pressing SPACE to move your car!
            </p>
            <p className="text-sm text-gray-500">
              Current difficulty: <span className={`font-semibold ${currentDifficulty.color}`}>
                Level {difficulty} - {currentDifficulty.name}
              </span>
            </p>
          </div>
        )}

        {gameStatus === 'racing' && (
          <div>
            <p className="text-lg font-semibold mb-2">Race in Progress!</p>
            <p className="text-sm text-gray-600 mb-2">
              Keep pressing SPACE to move your car forward!
            </p>
            <p className="text-sm text-gray-500">
              Difficulty: <span className={`font-semibold ${currentDifficulty.color}`}>
                Level {difficulty} - {currentDifficulty.name}
              </span>
            </p>
          </div>
        )}

        {gameStatus === 'finished' && (
          <div>
            <div className="flex items-center justify-center mb-2">
              <Trophy className="mr-2 text-yellow-500" size={24} />
              <p className="text-lg font-semibold">
                {winner === 'player' ? 'You Win!' : 'Computer Wins!'}
              </p>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              {winner === 'player'
                ? `Great job! You beat the computer on ${currentDifficulty.name} difficulty!`
                : `The computer won on ${currentDifficulty.name} difficulty. Try again!`}
            </p>
            {winner === 'player' && difficulty < 7 && (
              <p className="text-sm text-blue-600 font-medium">
                🎉 Ready for Level {difficulty + 1}? Try the next difficulty!
              </p>
            )}
          </div>
        )}
      </div>

      {/* Control Buttons */}
      <div className="flex justify-center space-x-4">
        {gameStatus === 'ready' && (
          <button
            onClick={startRace}
            className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
          >
            Start Race
          </button>
        )}

        {gameStatus === 'racing' && (
          <button
            onClick={movePlayer}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Move Forward (or press SPACE)
          </button>
        )}

        {gameStatus === 'finished' && (
          <button
            onClick={startRace}
            className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
          >
            Race Again
          </button>
        )}

        <button
          onClick={resetGame}
          className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors flex items-center"
        >
          <RotateCcw size={16} className="mr-2" />
          Reset All
        </button>
      </div>

      {/* Instructions */}
      <div className="mt-4 p-3 bg-gray-50 rounded-md text-sm text-gray-600">
        <p><strong>How to play:</strong> Press SPACE repeatedly to move your car forward. Race against the computer to reach the finish line first!</p>
        <p className="mt-1"><strong>Difficulty levels:</strong> Use the difficulty selector to adjust the computer's speed and make the game more challenging!</p>
      </div>
    </div>
  );
};
